import {
  Box,
  Button,
  CircularProgress,
  Modal,
  Stack,
  Typography,
} from "@mui/material";
import {
  GoogleMap,
  InfoWindow,
  LoadScript,
  Marker,
  OverlayView,
} from "@react-google-maps/api";
import { useEffect, useRef, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import { BricksType } from "../../../../../../domain/models/brick-management-report/brick-management-data";
import { BrickManagementStore } from "../../../../../../store/main/BrickManagementStore";

type Props = {
  open: boolean;
  handleClose: () => void;
  hq_id: string;
};

interface LocationMetadata {
  formatted_address: string;
  geometry: {
    location: {
      lat: number;
      lng: number;
    };
  };
  place_id: string;
}

interface HqBrick {
  id: number;
  name: string;
  brick_code: string;
  district: string;
  district_code: string;
  hq_territory_id: number;
  no_of_work_days: number;
  expected_business: number;
  distance_from_hq: number;
  station: string;
  status: string;
  approved_status: string;
  approved_by: number;
  created_at: string;
  updated_at: string;
  base_brick_id: number;
  distance_from_base_brick: number;
  description: string;
  locality: string;
}

interface BrickLocation {
  hq_brick: HqBrick;
  long_name: string;
  brick_id: number;
  location_metadata: LocationMetadata;
}

interface FetchBrickAreaLocationResponse {
  brick_locations: BrickLocation[];
}

const FetchHQWiseBrickAreaGeoTagging: React.FC<Props> = ({
  open,
  handleClose,
  hq_id,
}) => {
  const {
    fetchBrickAreaLocation,
    FetchBrickAreaLocationState,
    FetchBrickAreaLocation,
    BrickManagementData,
  } = BrickManagementStore();

  const [addedAreas, setAddedAreas] = useState<
    { formatted_address: string; place_id: string; lat: number; lng: number }[]
  >([]);

  const [markers, setMarkers] = useState<
    {
      lat: number;
      lng: number;
      place_id: string;
      brick_id: number;
      name: string;
      long_name: string;
    }[]
  >([]);
  const [center, setCenter] = useState<
    { lat: number; lng: number } | undefined
  >({ lat: 21.7679, lng: 78.8718 });
  const [zoom, setZoom] = useState<number>(4);
  const [brickID, setBrickID] = useState<{ brick_id: number; name: string }[]>(
    []
  );
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [selectedBrickName, setSelectedBrickName] = useState<number>();
  const [hoveredBrickId, setHoveredBrickId] = useState<string>("");
  const hoverTimeout = useRef<NodeJS.Timeout | null>(null);
  const [brickNameLoading, setBrickNameLoading] = useState<boolean>(true);

  const handleMarkerHover = (long_name: string) => {
    if (hoverTimeout.current) clearTimeout(hoverTimeout.current);
    setHoveredBrickId(long_name);
  };

  const handleMarkerOut = () => {
    hoverTimeout.current = setTimeout(() => {
      setHoveredBrickId("");
    }, 200);
  };

  useEffect(() => {
    return () => {
      if (hoverTimeout.current) clearTimeout(hoverTimeout.current);
    };
  }, []);



  useEffect(() => {
    brickHQwiseGeoTagging();
  }, []);

  useEffect(() => {
    if (markers.length > 0 && setBrickID.length > 0) {
      setIsExpanded(true);
    } else {
      setIsExpanded(false);
    }
  }, [markers, addedAreas]);

  const brickHQwiseGeoTagging = async () => {
    try {
      setBrickNameLoading(true);
      const params = {
        headquarter_id: hq_id,
      };
      const response = await fetchBrickAreaLocation(params);
      if (response.success) {
        const markersData = response.brick_locations.map(
          (location: BrickLocation) => ({
            lat: location.location_metadata.geometry.location.lat,
            lng: location.location_metadata.geometry.location.lng,
            place_id: location.location_metadata.place_id,
            brick_id: location.brick_id,
            name: location.hq_brick.name,
            long_name: location.long_name,
          })
        );
        setMarkers(markersData);
        if (markersData.length > 0) {
          setCenter({
            lat: markersData[0].lat,
            lng: markersData[0].lng,
          });
          setZoom(12);
        }
        const uniqueBrickNames: {
          brick_id: number;
          name: string;
          long_name: string;
        }[] = Array.from(
          new Map(
            response.brick_locations.map((location: BrickLocation) => [
              location.brick_id,
              {
                brick_id: location.brick_id,
                name: location.hq_brick.name,
                long_name: location.long_name,
              },

              
            ])
          ).values()
        ) as { brick_id: number; name: string; long_name: string }[];
        setBrickID(uniqueBrickNames);
      }
    } catch (error) {
    } finally {
      setBrickNameLoading(false);
    }
  };


  return (
    <>
      <Modal open={open} onClose={handleClose}>
        <Box
          position={"absolute"}
          top={"50%"}
          left={"50%"}
          bgcolor={"background.paper"}
          borderRadius={"22px"}
          sx={{
            transform: "translate(-50%, -50%)",
            display: "flex",
            overflow: "hidden",
          }}
          width="100%"
          p={3}
        >
          <Box display="flex" flexDirection="row" width="100%" height="100%">
            <Box flex="1">
              <Box
                display="flex"
                position={"absolute"}
                top={20}
                zIndex={1}
                bgcolor={"white"}
                marginTop={8}
                marginLeft={2}
                width={"30%"}
              >
                <Box flex="1" marginRight={2}>
                  <Box pl={2} marginBottom={2}>
                    <Typography
                      fontWeight={700}
                      variant="h6"
                      textAlign="left"
                      marginBottom={1}
                    >
                      Brick Names
                    </Typography>

                    <Box
                      position="absolute"
                      top={5}
                      right={16}
                      zIndex="tooltip"
                    >
                      <Button
                        onClick={() => handleClose()}
                        sx={{ height: "30px", width: "30px", minWidth: "30px" }}
                      >
                        <CloseIcon fontSize="medium" color="info" />
                      </Button>
                    </Box>
                  </Box>

                  <Box
                    pl={2}
                    sx={{
                      overflowY: "auto",
                      maxHeight: "40vh",
                      marginBottom: 3,
                    }}
                  >
                    <Stack spacing={1}>
                      <Box sx={{ mt: 1 }}>
                        <Stack spacing={1}>
                          {brickNameLoading ? (
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                minHeight: 100,
                                width: "100%",
                              }}
                            >
                              <CircularProgress size={32} />
                            </Box>
                          ) : brickID.length === 0 ||
                            !brickID.some(
                              (brick) => brick.brick_id && brick.name
                            ) ? (
                            <Typography variant="body1" color="textSecondary">
                              There is no brick created which has Geo Tagging.
                            </Typography>
                          ) : (
                            brickID
                              .filter((brick) => brick.brick_id && brick.name)
                              .map((brick) => (
                                <Button
                                  key={brick.brick_id}
                                  variant={
                                    selectedBrickName === brick.brick_id
                                      ? "contained"
                                      : "outlined"
                                  }
                                  color="primary"
                                  size="small"
                                  fullWidth
                                  onClick={() => {
                                    const markerToCenter = markers.find(
                                      (marker) =>
                                        marker.brick_id === brick.brick_id
                                    );
                                    if (markerToCenter) {
                                      setCenter({
                                        lat: markerToCenter.lat,
                                        lng: markerToCenter.lng,
                                      });
                                      setZoom(14);
                                    }
                                    setSelectedBrickName(brick.brick_id);
                                  }}
                                >
                                  {brick.name}
                                </Button>
                              ))
                          )}
                        </Stack>
                      </Box>
                    </Stack>
                  </Box>
                </Box>
              </Box>
              <LoadScript
                googleMapsApiKey={"AIzaSyCv-70Q4VJ5VZYPzQs06SuUiA-QX0195Nw"}
              >
                <GoogleMap
                  mapContainerStyle={{ height: "100vh", width: "100%" }}
                  center={center}
                  zoom={zoom}
                >
                  {markers.map((marker) => (
                    <Marker
                      key={marker.brick_id}
                      position={{ lat: marker.lat, lng: marker.lng }}
                      onMouseOver={() => handleMarkerHover(marker.long_name)}
                      onMouseOut={handleMarkerOut}
                      icon={{
                        url:
                          selectedBrickName === marker.brick_id
                            ? "http://maps.google.com/mapfiles/ms/icons/blue-dot.png"
                            : "http://maps.google.com/mapfiles/ms/icons/red-dot.png",
                      }}
                    >
                      {hoveredBrickId === marker.long_name && (
                        <OverlayView
                          position={{ lat: marker.lat, lng: marker.lng }}
                          mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                        >
                          <Box
                            sx={{
                              background: "#fff",
                              borderRadius: "8px",
                              padding: "8px 12px",
                              boxShadow: 4,
                              width: "100px",
                              height: "20px",
                              textAlign: "center",
                            }}
                          >
                            <Typography
                              variant="subtitle2"
                              sx={{ fontWeight: 600, color: "#212121" }}
                            >
                              {marker.long_name}
                            </Typography>
                          </Box>
                        </OverlayView>
                      )}
                    </Marker>
                  ))}
                </GoogleMap>
              </LoadScript>
            </Box>
          </Box>
        </Box>
      </Modal>
    </>
  );
};
export default FetchHQWiseBrickAreaGeoTagging;
